import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { AlertCircle } from "lucide-react";
import axios from "axios";

const API = "/api";

export const TradePanel = ({ currentPrice, account, fibonacci, tradeState }) => {
  const [size, setSize] = useState(2.0);
  const [limitsBias, setLimitsBias] = useState("no_trade");

  useEffect(() => {
    const fetchBias = async () => {
      try {
        const res = await axios.get(`${API}/strategy/state`);
        setLimitsBias(res.data.limits_bias || "no_trade");
      } catch (e) {}
    };
    fetchBias();
    const interval = setInterval(fetchBias, 5000);
    return () => clearInterval(interval);
  }, []);

  const entryPrice  = fibonacci?.levels?.find(l => l.role === "entry")?.price ?? null;
  // Use ATR-based stop from trade state when armed, otherwise show 61.8% from fib
  const stopPrice   = tradeState?.current_stop ?? fibonacci?.levels?.find(l => l.role === "stop")?.price ?? null;
  const targetPrice = fibonacci?.levels?.find(l => l.role === "target")?.price ?? null;

  const riskPerUnit   = entryPrice && stopPrice  ? Math.abs(entryPrice - stopPrice)  : null;
  const rewardPerUnit = entryPrice && targetPrice ? Math.abs(targetPrice - entryPrice) : null;
  const riskDollars   = riskPerUnit   ? (riskPerUnit   * size).toFixed(2) : null;
  const rewardDollars = rewardPerUnit ? (rewardPerUnit * size).toFixed(2) : null;
  const rrRatio       = riskPerUnit && rewardPerUnit ? (rewardPerUnit / riskPerUnit).toFixed(2) : null;

  const canTrade = limitsBias === "long_only" || limitsBias === "short_only";
  const direction = limitsBias === "long_only" ? "LONG" : limitsBias === "short_only" ? "SHORT" : null;

  return (
    <div className="space-y-5" data-testid="trade-panel">

      {/* Current Price */}
      <div className="text-center pb-4 border-b border-zinc-800">
        <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Current Price</div>
        <div className="font-mono text-2xl font-semibold" data-testid="trade-panel-price">
          ${currentPrice.toFixed(2)}
        </div>
      </div>

      {/* Trade Status */}
      {direction && (
        <div className={`flex items-center justify-between py-2 px-3 rounded border text-xs font-mono ${
          direction === "LONG"  ? "bg-green-900/20 border-green-900/40 text-green-400" :
          "bg-red-900/20 border-red-900/40 text-red-400"
        }`}>
          <span className="uppercase tracking-wider">{direction} TRADE ACTIVE</span>
          <span className="font-semibold">ENGINE {direction === "LONG" ? "A/B" : "A/B"}</span>
        </div>
      )}

      {/* Fib Levels — read only */}
      <div className="space-y-2">
        <div className="text-xs text-zinc-500 uppercase tracking-wider">Fib Levels (Auto)</div>
        <div className="space-y-1.5">
          {[
            { label: "Entry / 50%",       price: entryPrice,  color: "text-fuchsia-400" },
            { label: "Stop / 61.8 Area",  price: stopPrice,   color: "text-red-400" },
            { label: "Target / -23.6%",   price: targetPrice, color: "text-green-400" },
          ].map(({ label, price, color }) => (
            <div key={label} className="flex items-center justify-between py-1.5 px-3 bg-zinc-900 rounded border border-zinc-800">
              <span className="text-xs text-zinc-500">{label}</span>
              <span className={`font-mono text-sm font-semibold ${color}`}>
                {price ? `$${price.toFixed(2)}` : "--"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Position Size */}
      <div className="space-y-2">
        <Label className="text-xs text-zinc-500 uppercase tracking-wider">Position Size</Label>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            value={size}
            onChange={(e) => setSize(parseFloat(e.target.value) || 0.01)}
            step="0.01"
            min="0.01"
            max="10"
            className="font-mono bg-zinc-900 border-zinc-800"
            data-testid="position-size-input"
          />
          <span className="text-xs text-zinc-500 font-mono">BTC</span>
        </div>
        <Slider
          value={[size]}
          onValueChange={([v]) => setSize(v)}
          min={0.01}
          max={1}
          step={0.01}
          className="py-1"
        />
      </div>

      {/* Risk / Reward — auto calculated from fib levels */}
      <div className="space-y-1.5">
        <div className="flex justify-between text-xs py-1">
          <span className="text-zinc-500">Risk</span>
          <span className="font-mono text-red-400">{riskDollars ? `$${riskDollars}` : "--"}</span>
        </div>
        <div className="flex justify-between text-xs py-1">
          <span className="text-zinc-500">Reward</span>
          <span className="font-mono text-green-400">{rewardDollars ? `$${rewardDollars}` : "--"}</span>
        </div>
        <div className="flex items-center justify-between py-2 px-3 bg-zinc-900 rounded border border-zinc-800">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-3.5 h-3.5 text-zinc-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">R:R</span>
          </div>
          <span className={`font-mono text-sm font-semibold ${
            rrRatio && parseFloat(rrRatio) >= 2 ? "text-green-500" :
            rrRatio && parseFloat(rrRatio) >= 1 ? "text-yellow-500" : "text-zinc-500"
          }`}>
            {rrRatio ? `1:${rrRatio}` : "--"}
          </span>
        </div>
      </div>

      {/* System note */}
      <div className="text-xs text-zinc-600 text-center pt-2 border-t border-zinc-800">
        LEVELS trades automatically on fib structure.<br/>
        Entry, stop and target are rule-based — not manual.
      </div>

      {/* Account Info */}
      {account && (
        <div className="pt-2 border-t border-zinc-800">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Available</div>
              <div className="font-mono text-sm font-semibold">${account.balance?.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Equity</div>
              <div className="font-mono text-sm font-semibold">${account.equity?.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
